'use client';

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { areaAPI, propertyTypeAPI } from "@/lib/api";

export function InteriorHero() {
    const [formData, setFormData] = useState({
        area: '',
        propertyType: '',
        bedrooms: '',
        bathrooms: '',
        furnitureType: ''
    });

    const [areas, setAreas] = useState<any[]>([]);
    const [propertyTypes, setPropertyTypes] = useState<any[]>([]);

    const bedroomOptions = ['studio', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
    const bathroomOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [areasResp, typesResp] = await Promise.all([
                    areaAPI.getFrontendList(),
                    propertyTypeAPI.getAllWeb('property_type')
                ]);

                const areasData = (areasResp as any)?.data;
                const areasList = (areasData?.data ?? areasData?.items ?? areasData?.results ?? areasData) as any[];
                setAreas(Array.isArray(areasList) ? areasList : []);

                const typesData = (typesResp as any)?.data;
                const typesList = (typesData?.data ?? typesData?.items ?? typesData?.results ?? typesData) as any[];
                setPropertyTypes(Array.isArray(typesList) ? typesList : []);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        console.log('Form submitted:', formData);
        // Handle form submission here
    };

    return (
        <section className="relative flex items-center justify-center min-h-[90vh] py-20">
            <div className="absolute inset-0 z-0">
                <img src="/images/interior/luxury-modern-living-room-with-elegant-furniture-a.jpg" alt="Luxury interior design" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-secondary/60 via-secondary/40 to-secondary" />
            </div>

            <div className="container mx-auto px-4 lg:px-8 relative z-10">
                <div className="text-center mb-12">
                    <h1 className="text-5xl font-heading font-bold text-white mb-6 text-balance leading-tight">
                        Transform Your Space Into
                        <span className="block text-primary mt-2">A Work of Art</span>
                    </h1>
                    {/* <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10 text-pretty leading-relaxed">
                        Bespoke interior design services that blend timeless elegance with contemporary sophistication. We create
                        spaces that inspire and endure.
                    </p> */}
                </div>

                {/* Form Card */}
                <div className="max-w-4xl mx-auto bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 md:p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Select Area */}
                            <div>
                                <Label htmlFor="area" className="text-gray-700 font-medium mb-2 block">
                                    Select Area *
                                </Label>
                                <Select
                                    value={formData.area}
                                    onValueChange={(value) => handleInputChange('area', value)}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select area" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {areas.map((area) => (
                                            <SelectItem key={area._id || area.id} value={String(area._id || area.id)}>
                                                {area.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Property Type */}
                            <div>
                                <Label htmlFor="propertyType" className="text-gray-700 font-medium mb-2 block">
                                    Property Type *
                                </Label>
                                <Select
                                    value={formData.propertyType}
                                    onValueChange={(value) => handleInputChange('propertyType', value)}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select property type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {propertyTypes.map((type) => (
                                            <SelectItem key={type._id || type.id} value={String(type._id || type.id)}>
                                                {type.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Select Beds */}
                            <div>
                                <Label htmlFor="bedrooms" className="text-gray-700 font-medium mb-2 block">
                                    Select Beds *
                                </Label>
                                <Select
                                    value={formData.bedrooms}
                                    onValueChange={(value) => handleInputChange('bedrooms', value)}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select beds" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {bedroomOptions.map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {option === 'studio' ? 'Studio' : `${option} Bedroom${option !== '1' ? 's' : ''}`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Select Beds */}
                            {/* <div>
                                <Label htmlFor="bedrooms" className="text-gray-700 font-medium mb-2 block">
                                    Select Beds *
                                </Label>
                                <Select 
                                    value={formData.bedrooms} 
                                    onValueChange={(value) => handleInputChange('bedrooms', value)}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select beds" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {bedroomOptions.map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {option === 'studio' ? 'Studio' : `${option} Bedroom${option !== '1' ? 's' : ''}`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div> */}

                            {/* Select Baths */}
                            <div>
                                <Label htmlFor="bathrooms" className="text-gray-700 font-medium mb-2 block">
                                    Select Baths *
                                </Label>
                                <Select
                                    value={formData.bathrooms}
                                    onValueChange={(value) => handleInputChange('bathrooms', value)}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select baths" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {bathroomOptions.map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {option} Bathroom{option !== '1' ? 's' : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Furniture Type */}
                            <div>
                                <Label htmlFor="furnitureType" className="text-gray-700 font-medium mb-2 block">
                                    Furniture Type *
                                </Label>
                                <Select
                                    value={formData.furnitureType}
                                    onValueChange={(value) => handleInputChange('furnitureType', value)}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select furniture type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="executive">Executive</SelectItem>
                                        <SelectItem value="luxury">Luxury</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            // size="lg"
                            className="w-full"
                        >
                            Start Your Project
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                    </form>
                </div>
            </div>
        </section>
    )
}
